// =============================================================================
// GameApp.ts — Cola geral do cliente: telas, teclado, rede, renderização e som.
// Fluxo: MENU -> LOBBY -> JOGO -> GAME OVER -> volta às urnas.
// =============================================================================
import { EV, MSG } from '../../shared/protocol';
import type {
  GameEvent, GameOverPayload, GameStartPayload, GameStartPlayer,
  LobbySnapshot, MatchClearedPayload, RematchPayload, Snapshot,
  UrnaCharges, UrnaChoiceDisplay,
} from '../../shared/protocol';
import { URNA_TIME } from '../../shared/constants';
import { BOSS_TABLE } from '../../shared/enemies';
import { DEFAULT_SKIN, sanitizeSkin } from '../../shared/skins';
import { WEAPON_TABLE } from '../../shared/weapons';
import type { EleicaoMode, EleicaoStatusResponse } from '../../shared/eleicao';
import type { MetaProfile } from '../../shared/meta';
import type { FeitosState } from '../../shared/feitos';
import { fetchAuthState, loginWithGoogle, logout } from '../infrastructure/AuthClient';
import type { AuthState } from '../infrastructure/AuthClient';
import {
  creditLocalFeitos, creditLocalProfile, fetchPerfil, metaJoinState,
} from '../infrastructure/MetaClient';
import { NetClient } from '../infrastructure/NetClient';
import type { MoveVector } from '../infrastructure/NetClient';
import { Renderer } from '../presentation/Renderer';
import { audio } from '../presentation/AudioEngine';
import { $, $input, showScreen, type Overlay } from '../presentation/ui/dom';
import { CharGrid } from '../presentation/ui/CharGrid';
import { MenuView } from '../presentation/ui/MenuView';
import { TitleView } from '../presentation/ui/TitleView';
import { LobbyView } from '../presentation/ui/LobbyView';
import { HudView } from '../presentation/ui/HudView';
import { MinimapView } from '../presentation/ui/MinimapView';
import { StatsView } from '../presentation/ui/StatsView';
import { UrnaView } from '../presentation/ui/UrnaView';
import { CasinoView } from '../presentation/ui/CasinoView';
import { EleicaoView } from '../presentation/ui/EleicaoView';
import { GameOverView } from '../presentation/ui/GameOverView';
import { MatchClearedView } from '../presentation/ui/MatchClearedView';
import { GabineteView } from '../presentation/ui/GabineteView';
import { DiarioView } from '../presentation/ui/DiarioView';
import { ArquivoView } from '../presentation/ui/ArquivoView';
import { ConquistasView } from '../presentation/ui/ConquistasView';
import { renderDossies } from '../presentation/ui/dossies';
import { RankingView } from '../presentation/ui/RankingView';
import { ConnView } from '../presentation/ui/ConnView';
import { TouchControlsView } from '../presentation/ui/TouchControlsView';
import { InputController } from './InputController';

// Última skin escolhida POR personagem (issue #24), persistida entre sessões
const SKINS_KEY = 'bs_skins';

function loadSkinPrefs(): Record<string, string> {
  try {
    const raw = JSON.parse(localStorage.getItem(SKINS_KEY) ?? '{}') as Record<string, unknown>;
    const prefs: Record<string, string> = {};
    for (const [charId, skinId] of Object.entries(raw)) {
      const clean = sanitizeSkin(charId, skinId);
      if (clean !== DEFAULT_SKIN) prefs[charId] = clean;
    }
    return prefs;
  } catch {
    return {};
  }
}

export class GameApp {
  private readonly net = new NetClient();
  private renderer: Renderer | null = null;

  private youId: string | null = null;
  private selectedChar = 'lula';
  private readonly skinPrefs = loadSkinPrefs();   // charId -> skinId escolhida
  private playersInfo = new Map<string, GameStartPlayer>();   // id -> info da partida atual
  private duration = 1200;
  private lastHp: number | null = null;   // detecta dano recebido p/ vinheta + som
  private lastXp: number | null = null;   // detecta coleta de voto p/ som
  private lastLvl: number | null = null;
  private lastC2: number | null = null;   // detecta maço de Caixa 2 embolsado (#94)
  private profile: MetaProfile | null = null;  // perfil de meta-progressão (#90/#94)
  private inLobby = false;
  private ready = false;
  private lastSnap: Snapshot | null = null;     // último snapshot (p/ placar do TAB)
  private lastLobby: LobbySnapshot | null = null;

  // Views
  private readonly menu = new MenuView({
    onCreate: () => void this.createRoom(),
    onJoinCode: (code) => void this.joinByCode(code),
    onEleicao: (mode) => this.eleicao.show(mode, this.selectedChar),
    onBack: () => showScreen('title'),
  });
  // Eleições com seed (#101/#97): tela pré-partida com o edital do período
  private readonly eleicao = new EleicaoView({
    onBack: () => showScreen('menu'),
    onStart: (mode, charId) => void this.startEleicaoRun(mode, charId),
    onCharPick: (charId) => { this.selectedChar = charId; },
  });
  private readonly lobby = new LobbyView({
    onReadyToggle: () => this.toggleReady(),
    onStart: () => this.net.startGame(),
    onDifficulty: (id) => {
      audio.sfx.urnaKey();
      this.net.setDifficulty(id);
    },
    onBack: () => this.leaveLobby(),
  });
  private readonly title = new TitleView();
  private readonly hud = new HudView();
  private readonly minimap = new MinimapView();
  private readonly stats = new StatsView();
  private readonly urna = new UrnaView();
  private readonly casino = new CasinoView();
  private readonly gameOver = new GameOverView();
  private readonly cleared = new MatchClearedView();
  // Overlays de partida no seam Overlay (raiz única no index.html + hide):
  // a REELEIÇÃO fecha todos de uma vez. A urna fica FORA — ela gerencia duas
  // raízes (#urna e #pause-overlay); o aviso de conexão também — o rematch não
  // pode engolir o overlay de queda.
  private readonly overlays: Overlay[] = [this.gameOver, this.cleared, this.casino, this.stats];
  // Conquistas e Arquivos (#122): a tela com abas que reúne o Diário Oficial
  // (#99), os Arquivos da CPI (#95) e o Gabinete da Campanha (#94) — abre da
  // tela inicial quando logado; VOLTAR devolve pra ela. O callback de perfil
  // do Gabinete mantém os cadeados do CharGrid em dia (#90).
  private readonly conquistas = new ConquistasView({
    diario: new DiarioView(),
    arquivo: new ArquivoView(),
    gabinete: new GabineteView((profile) => this.applyProfile(profile)),
  }, () => showScreen('title'));
  // Ranking (issue #88): abre da tela de título; VOLTAR devolve pra ela
  private readonly ranking = new RankingView(() => showScreen('title'));
  private readonly conn = new ConnView();
  private readonly lobbyCharGrid = new CharGrid(this.lobby.charGridEl, true);

  // Direcional virtual: só ativa em tela touch (no desktop é um no-op)
  private readonly touch = new TouchControlsView();

  private readonly input = new InputController({
    urnaVisible: () => this.urna.visible(),
    onUrnaKey: (idx) => {
      audio.sfx.urnaKey();
      this.urna.pick(idx);
    },
    onStatsShow: () => {
      if (this.renderer) this.stats.show(this.lastSnap, this.playersInfo, this.youId, this.net.getPing());
    },
    onStatsHide: () => this.stats.hide(),
    onPauseKey: () => {
      if (this.renderer) this.net.togglePause(); // só em jogo; o servidor valida o resto
    },
    onDigitCode: (code) => {
      // Só em partida viva — o servidor confere o código e aplica o poder
      if (this.renderer) this.net.sendEasterCode(code);
    },
  });

  start(): void {
    // Tela de abertura: só o vídeo + cabine de identificação (login social).
    // As opções do menu chegam quando o /api/me responder.
    this.title.setStatus('CONSULTANDO O CARTÓRIO ELEITORAL...');
    void this.initAuth();

    this.refreshCharGrid();
    this.wireNet();
    this.wireAudioControls();
    this.urna.bindQuit(() => this.quitToMenu());
    // Pausa por toque (issue #105): botão do HUD e retomada no overlay mandam
    // o mesmo game:pause do Esc — o servidor continua decidindo se pode
    this.urna.bindResume(() => this.net.togglePause());
    $('btn-pause-touch').onclick = () => {
      if (this.renderer) this.net.togglePause();
    };

    // Perfil de meta-progressão (#90): cadeados do CharGrid dependem dele
    void this.refreshProfile();

    // Mural de salas abertas (atualiza enquanto o menu estiver na tela)
    void this.refreshRooms();
    setInterval(() => void this.refreshRooms(), 3000);

    // Tarja de ÚLTIMO DIA DE VOTAÇÃO no botão da Eleição da Semana (#97):
    // domingo antes das 17h de Brasília é a última chance de pontuar.
    // Reavalia a cada minuto: aba aberta na virada acende/apaga sozinha.
    void this.refreshEleicaoBadge();
    setInterval(() => void this.refreshEleicaoBadge(), 60_000);

    // Reconexão automática: F5 no meio da partida não derruba seu mandato
    void this.tryResume();
  }

  private async refreshEleicaoBadge(): Promise<void> {
    try {
      const res = await fetch('/api/eleicao');
      const data = (await res.json()) as EleicaoStatusResponse;
      // Liga E desliga: a tarja não pode continuar piscando na segunda-feira
      if (data?.ok) $('menu-eleicao-tarja').style.display = data.semanal?.ultimoDia ? '' : 'none';
    } catch { /* edital fora do ar: mantém a tarja como está */ }
  }

  // Eleição do Dia/da Semana (#101/#97): sala solo com seed, sem lobby —
  // o servidor responde o ack e emite o game:start na sequência
  private async startEleicaoRun(mode: EleicaoMode, charId: string): Promise<void> {
    this.eleicao.setError('');
    const res = await this.net.startEleicao(mode, this.menu.playerName(), charId);
    if (!res.ok) {
      this.eleicao.setError(res.error);
      this.eleicao.setStartEnabled(true); // destrava o botão pra tentar de novo
      return;
    }
    this.youId = res.playerId;
    this.net.saveSession(res.code, res.token);
  }

  // ---------------------------------------------------------------------------
  // Identificação do eleitor (login social — a tela de título é a cabine)
  // ---------------------------------------------------------------------------
  private async initAuth(): Promise<void> {
    // ?auth=err vem do callback OAuth quando a apuração do login dá errado
    const authErr = new URLSearchParams(location.search).get('auth') === 'err';
    if (authErr) history.replaceState(null, '', location.pathname);
    this.applyAuthState(await fetchAuthState(), authErr);
  }

  private applyAuthState(state: AuthState, authErr = false): void {
    // Conta (ou modo) definida: o perfil de meta-progressão pode ter mudado
    void this.refreshProfile();
    if (state.user) {
      const nome = state.user.name ?? 'Eleitor';
      const primeiroNome = nome.split(' ')[0];
      // Nome de urna já sai do título de eleitor (se o campo estiver vazio)
      const input = $input('input-name');
      if (!input.value) input.value = primeiroNome.slice(0, 16);
      // Logado: o "CONTINUAR COMO <nome>" já diz quem é; a linha de status
      // fica limpa (o min-height reservado segura o layout sem deslocar)
      this.title.setStatus('');
      this.title.setOptions([
        { label: `CONTINUAR COMO ${primeiroNome.toUpperCase()}`, action: () => showScreen('menu') },
        // Conquistas e Arquivos (#122): o acervo do mandato só aparece logado
        { label: 'CONQUISTAS E ARQUIVOS', action: () => this.conquistas.show() },
        { label: 'RANKING', action: () => this.ranking.show() },
        { label: 'SAIR DA CONTA', action: () => void this.logoutFlow() },
      ]);
    } else if (state.configured) {
      this.title.setStatus(authErr
        ? 'FRAUDE NA CABINE: o login falhou na apuração. Tente de novo.'
        : 'IDENTIFIQUE-SE PARA VOTAR', authErr);
      this.title.setOptions([
        { label: 'ENTRAR COM GOOGLE', icon: 'google', action: () => loginWithGoogle() },
        { label: 'RANKING', action: () => this.ranking.show() },
      ]);
    } else {
      // Servidor sem OAuth/Postgres configurado (dev local): segue o baile
      this.title.setStatus('LOGIN SOCIAL NÃO CONFIGURADO NESTE SERVIDOR');
      this.title.setOptions([
        { label: 'ENTRAR SEM TÍTULO (DEV)', action: () => showScreen('menu') },
        // Sem OAuth o modo dev faz as vezes da conta: o acervo fica acessível
        { label: 'CONQUISTAS E ARQUIVOS', action: () => this.conquistas.show() },
        { label: 'RANKING', action: () => this.ranking.show() },
      ]);
    }
  }

  private async logoutFlow(): Promise<void> {
    await logout();
    this.applyAuthState({ configured: true, user: null });
    this.title.setStatus('SESSÃO ENCERRADA — mandato devolvido ao povo');
  }

  // ---------------------------------------------------------------------------
  // Meta-progressão (#90/#94): perfil com votos vitalícios e Caixa 2
  // ---------------------------------------------------------------------------
  private async refreshProfile(): Promise<void> {
    const res = await fetchPerfil();
    if (res.ok && res.profile) this.applyProfile(res.profile);
    if (res.ok && res.feitos) this.applyFeitos(res.feitos);
  }

  private applyProfile(profile: MetaProfile): void {
    this.profile = profile;
    this.refreshCharGrid();
  }

  // Feitos (#92): as 3 pastas de dossiê ficam SEMPRE à vista no menu
  private applyFeitos(feitos: FeitosState): void {
    renderDossies($('menu-dossies'), feitos.dossies);
  }

  // ---------------------------------------------------------------------------
  // Seleção de personagem e skin (a escolha mora inteira no comitê — issue #67)
  // ---------------------------------------------------------------------------
  private skinOf(charId: string): string {
    return this.skinPrefs[charId] ?? DEFAULT_SKIN;
  }

  private setSkin(charId: string, skinId: string): void {
    if (skinId === DEFAULT_SKIN) delete this.skinPrefs[charId];
    else this.skinPrefs[charId] = skinId;
    localStorage.setItem(SKINS_KEY, JSON.stringify(this.skinPrefs));
  }

  private refreshCharGrid(): void {
    // Cadeados do Curral Eleitoral (#90): votos vitalícios do perfil atual
    this.lobbyCharGrid.setVotos(this.profile?.votos ?? null);
    this.lobbyCharGrid.render(this.selectedChar, (id) => {
      this.selectedChar = id;
      audio.sfx.urnaKey();
      audio.selectVoice(id);
      this.refreshCharGrid();
      this.net.setChar(id, this.skinOf(id));
    }, {
      skinOf: (charId) => this.skinOf(charId),
      onSkinChange: (charId, skinId) => {
        this.setSkin(charId, skinId);
        audio.sfx.urnaKey();
        this.refreshCharGrid();
        // Só o personagem em uso viaja pro servidor; os demais ficam guardados
        if (charId === this.selectedChar) this.net.setChar(charId, skinId);
      },
    });
    // Cartão do candidato no lobby responde na hora, sem esperar o servidor
    this.lobby.renderDetail(this.selectedChar, this.skinOf(this.selectedChar));
  }

  private async createRoom(): Promise<void> {
    this.menu.setError('');
    // O perfil local viaja no join (#90/#94): pro anônimo é a fonte dos bônus
    // e desbloqueios (sanitizada no servidor); logado, o servidor usa o Postgres
    const res = await this.net.create(this.menu.playerName(), this.selectedChar,
      this.skinOf(this.selectedChar), metaJoinState());
    if (!res.ok) return this.menu.setError(res.error);
    this.enterLobby(res.playerId, res.code, res.token);
  }

  private async joinByCode(code: string): Promise<void> {
    this.menu.setError('');
    const res = await this.net.join(code, this.menu.playerName(), this.selectedChar,
      this.skinOf(this.selectedChar), metaJoinState());
    if (!res.ok) return this.menu.setError(res.error);
    this.enterLobby(res.playerId, res.code, res.token);
  }

  private enterLobby(playerId: string, code: string, token: string): void {
    this.youId = playerId;
    this.net.saveSession(code, token);
    this.inLobby = true;
    showScreen('lobby');
    if (this.lastLobby) this.lobby.render(this.lastLobby, this.youId); // update pode ter chegado antes do ack
    // Nome de urna final conhecido: garante os cadeados certos no CharGrid (#90)
    void this.refreshProfile();
  }

  // Botão VOLTAR do comitê (issue #67): devolve a vaga no servidor, zera o
  // estado local da sala e reabre o mural de salas
  private leaveLobby(): void {
    this.net.leaveRoom();
    this.net.clearSession();
    this.inLobby = false;
    this.ready = false;
    this.lobby.setReadyState(false);
    this.lobby.setRematchNotice(null);
    this.youId = null;
    this.lastLobby = null;
    this.menu.setError('');
    showScreen('menu');
    void this.refreshRooms();
  }

  private async refreshRooms(): Promise<void> {
    if (!this.menu.isActive()) return;
    // Mede o ping junto com cada atualização do mural: todas as salas moram
    // no mesmo servidor, então o "ping da sala" é o seu próprio RTT
    const [ping, res] = await Promise.all([this.net.measurePing(), this.net.listRooms()]);
    if (res?.ok) this.menu.renderRoomList(res.rooms, ping, (code) => void this.joinByCode(code));
  }

  private toggleReady(): void {
    this.ready = !this.ready;
    this.net.setReady(this.ready);
    this.lobby.setReadyState(this.ready);
    audio.sfx.urnaKey();
  }

  // ---------------------------------------------------------------------------
  // Rede: lobby, partida, snapshots e eventos
  // ---------------------------------------------------------------------------
  private wireNet(): void {
    this.net.on(MSG.LOBBY_UPDATE, (lobby) => {
      this.lastLobby = lobby;
      if (this.inLobby) this.lobby.render(lobby, this.youId);
    });

    this.net.on(MSG.GAME_START, (payload) => this.startGame(payload));

    this.net.on(MSG.STATE, (snap) => this.onSnapshot(snap));

    // O servidor manda os eventos do tick em LOTE (array); mensagens privadas
    // (urna) e avisos de sala ainda chegam como evento único
    this.net.on(MSG.EVENT, (payload) => {
      for (const ev of Array.isArray(payload) ? payload : [payload]) this.handleEvent(ev);
    });

    // Apuração vencida (#85): mundo congelado no servidor; parabéns + escolha
    this.net.on(MSG.MATCH_CLEARED, (data) => this.showCleared(data));

    // Crédito de meta-progressão do ANÔNIMO (#90/#94): o servidor só manda
    // esta mensagem pra quem não tem conta — o perfil mora no localStorage
    // deste navegador e é somado aqui (vitória, derrota ou desistência).
    // Os feitos (#99/#92/#95) fundem com as MESMAS funções puras do servidor.
    this.net.on(MSG.META_CREDIT, (credit) => {
      let profile = creditLocalProfile(credit.votos, credit.caixa2);
      if (credit.feitos) {
        const r = creditLocalFeitos(credit.feitos);
        profile = r.profile;
        this.applyFeitos(r.estado);
      }
      this.applyProfile(profile);
    });

    this.net.on(MSG.GAME_OVER, (data) => this.showGameOver(data));

    // REELEIÇÃO aceita (#91): a sala inteira volta pro comitê com a mesma chapa
    this.net.on(MSG.REMATCH, (data) => this.onRematch(data));

    // Queda de conexão (issue #72): deploy no Coolify reinicia o servidor e o
    // jogo congelava sem aviso. Agora a queda vira overlay + o Socket.IO segue
    // tentando; quando voltar, retomamos a sessão ou voltamos ao menu.
    this.net.onDisconnect(() => this.onConnectionLost());
    this.net.onReconnect(() => void this.onReconnected());
  }

  // Fim de mandato (#91/#96): a sessão NÃO é descartada — a sala fica 'ended'
  // esperando a REELEIÇÃO; só sair de vez (VOLTAR ÀS URNAS) devolve o título
  // ao cartório. Também reconstrói a tela em reconexão/F5 com sala 'ended'.
  private showGameOver(data: GameOverPayload): void {
    this.net.stopInputLoop();
    this.stats.hide();
    audio.stopBgm();
    (data.victory ? audio.sfx.victory : audio.sfx.defeat)();
    this.gameOver.show(data, this.youId, {
      onExit: () => {
        this.net.clearSession();
        location.reload();
      },
      onRematch: async () => {
        const res = await this.net.requestRematch();
        return res.ok ? null : res.error;
      },
    });
  }

  // REELEIÇÃO (#91): o servidor devolveu a sala pro lobby com a mesma chapa.
  // Zera o estado de partida do cliente e reabre o comitê — código preservado,
  // personagens pré-selecionados; quem clicou já chega pronto.
  private onRematch(data: RematchPayload): void {
    this.net.stopInputLoop();
    for (const o of this.overlays) o.hide();
    this.urna.hide(); // fora do seam (duas raízes): fecha explícito como antes
    this.renderer?.destroy();
    this.renderer = null;
    this.lastSnap = null;
    this.lastHp = null;
    this.lastXp = null;
    this.lastLvl = null;

    const fuiEu = data.byId === this.youId;
    this.ready = fuiEu;
    this.lobby.setReadyState(this.ready);
    // O texto do aviso é montado no render do lobby (host vê INICIAR, os
    // demais veem PRONTO) e some se quem convocou abandonar o comitê
    this.lobby.setRematchNotice(fuiEu ? null : { by: data.by, byId: data.byId });
    this.inLobby = true;
    showScreen('lobby');
    if (this.lastLobby) this.lobby.render(this.lastLobby, this.youId); // o lobby:update fresco vem logo atrás
    // O comitê reabre com o personagem que o SERVIDOR tem pra você em foco na
    // grade (issue #135) — numa reeleição vinda da Eleição do Dia o Voto
    // Impresso pode ter trocado o candidato sem a grade do comitê saber.
    const you = this.lastLobby?.players.find((p) => p.id === this.youId);
    if (you) this.selectedChar = you.charId;
    this.refreshCharGrid();
    // Perfil re-buscado: candidato destravado na run anterior já sai do sigilo
    void this.refreshProfile();
    audio.startMenuMusic();
  }

  private onConnectionLost(): void {
    this.net.stopInputLoop(); // sem servidor não há tick — input viraria fila morta
    this.conn.showReconnecting();
  }

  private async onReconnected(): Promise<void> {
    const session = this.net.loadSession();
    if (!session?.code || !session?.token) { this.conn.hide(); return; } // estava fora de sala

    const res = await this.net.reconnect(session.code, session.token);
    if (res.ok) {
      this.conn.hide();
      this.youId = res.playerId;
      if (res.resume) {
        // Partida seguiu sem você (vaga guardada por 2min): reconstrói tudo
        this.startGame(res.resume.start, res.resume.snapshot,
          { choices: res.resume.pendingOffer, time: res.resume.offerTime, charges: res.resume.offerCharges },
          res.resume.cleared);
      } else if (res.state === 'ended') {
        // Sala em game over esperando REELEIÇÃO (#91): o canal foi religado e
        // a sessão segue válida — a piscada de wifi não cassa a reeleição.
        if (this.gameOver.visible()) {
          // Um pedido de rematch pode ter morrido junto com o socket: o botão
          // volta a ficar clicável (a sala está viva, a chapa continua na tela)
          this.gameOver.enableRematch();
        } else if (res.gameOver) {
          // A partida acabou enquanto você estava fora: reconstrói o game over
          showScreen('game');
          this.showGameOver(res.gameOver);
        }
      } else {
        this.inLobby = true;
        showScreen('lobby');
        if (this.lastLobby) this.lobby.render(this.lastLobby, this.youId);
      }
      return;
    }

    // Na tela de game over a sessão só existia pra REELEIÇÃO (#91): se a sala
    // se foi (reinício/arquivada), desativa o botão e deixa o eleitor ler em paz
    if (this.gameOver.visible()) {
      this.net.clearSession();
      this.conn.hide();
      this.gameOver.disableRematch('O servidor reiniciou — a sala foi arquivada. Sem reeleição desta vez.');
      return;
    }

    // Sala não existe mais: o servidor reiniciou do zero (deploy). Anula o
    // pleito com aviso e recarrega — antes o jogo só congelava (issue #72)
    this.net.clearSession();
    audio.stopBgm();
    this.conn.showEnded();
    setTimeout(() => location.reload(), 3500);
  }

  // Tela de parabéns (#85): o input segue ativo (o mundo está congelado no
  // servidor) — SAIR repete o caminho do game over; MODO INFINITO segue o baile
  private showCleared(data: MatchClearedPayload): void {
    this.stats.hide();
    audio.sfx.victory();
    this.cleared.show(data, this.youId,
      () => {
        this.net.stopInputLoop();
        this.net.clearSession();
        location.reload();
      },
      () => {
        this.net.startInfinite();
        this.cleared.hide(); // os demais fecham no EV.INFINITE_ON
      });
  }

  // Botão do recesso (solo): avisa o servidor pra encerrar a partida e recarrega
  // a página de volta ao menu (mesmo caminho do game over). O await do ack
  // garante que o meta:credit do anônimo já atualizou o localStorage (#90/#94)
  private quitToMenu(): void {
    void (async () => {
      await this.net.quitGame();
      this.net.stopInputLoop();
      this.net.clearSession();
      location.reload();
    })();
  }

  // Início de jogo (novo ou retomado por reconexão)
  private startGame(
    payload: GameStartPayload,
    resumeSnapshot: Snapshot | null = null,
    pendingOffer: { choices: UrnaChoiceDisplay[] | null; time: number; charges: UrnaCharges | null } | null = null,
    clearedData: MatchClearedPayload | null = null,   // caiu na tela de parabéns (#85)?
  ): void {
    this.inLobby = false;
    this.lobby.setRematchNotice(null);  // o convite da REELEIÇÃO cumpriu o papel
    this.duration = payload.duration;
    this.playersInfo = new Map(payload.players.map((p) => [p.id, p]));

    showScreen('game');
    this.gameOver.hide();
    this.cleared.hide();
    this.casino.hide();
    this.hud.showControlsHint();
    // Botão de pausa touch só na partida solo: com 2+ o servidor ignora o
    // recesso ("ninguém segura o relógio da democracia sozinho")
    $('btn-pause-touch').classList.toggle('solo', payload.players.length === 1);
    this.renderer?.destroy();
    this.renderer = new Renderer($('game-canvas'));
    this.renderer.startGame(payload, this.youId ?? '', () => this.moveVector());
    this.minimap.start(payload.arena, this.youId ?? '', this.playersInfo);

    // Eleições com seed (#101/#97): selo no HUD, aviso de amistoso e o
    // apagão do minimapa quando o Toffoli mandou (também na reconexão)
    const elei = payload.eleicao ?? null;
    this.minimap.setEnabled(!elei?.modifiers.includes('toffoli_apagou'));
    this.hud.setEleicaoBadge(elei
      ? `${elei.mode === 'diaria' ? 'ELEIÇÃO DO DIA' : 'ELEIÇÃO DA SEMANA'}${elei.scoring ? '' : ' - AMISTOSA'}`
      : null);
    if (elei && !elei.scoring) {
      this.hud.feed('Partida AMISTOSA: esta tentativa não entra no placar do período.', true);
    }
    if (elei?.modifiers.includes('toffoli_apagou')) {
      this.hud.feed('TOFFOLI APAGOU: minimapa sob sigilo de 100 anos.', true);
    }
    if (resumeSnapshot) {
      this.net.syncInputSequence(resumeSnapshot.ack);
      this.renderer.onSnapshot(resumeSnapshot);
    }
    if (pendingOffer?.choices) this.offerUrna(pendingOffer.choices, pendingOffer.time, pendingOffer.charges);
    if (clearedData) this.showCleared(clearedData);   // reabre a escolha do infinito

    this.lastHp = null;
    this.lastXp = null;
    this.lastLvl = null;
    this.lastC2 = null;
    this.net.startInputLoop(() => this.moveVector());
    audio.startBgm();
  }

  // Vetor de movimento unificado: teclado manda; sem tecla apertada, vale o
  // polegar no direcional virtual (celular). Ambos já saem normalizados.
  private moveVector(): MoveVector {
    const kb = this.input.vector();
    return kb.mx || kb.my ? kb : this.touch.vector();
  }

  private onSnapshot(snap: Snapshot): void {
    if (!this.renderer) return;
    this.lastSnap = snap;
    this.renderer.onSnapshot(snap);
    this.hud.update(snap, this.youId, this.playersInfo, this.duration);
    this.minimap.update(snap);
    this.updatePerfHud();
    if (this.stats.visible()) this.stats.show(snap, this.playersInfo, this.youId, this.net.getPing()); // placar ao vivo

    // Pausa democrática: alguém na urna congela o mundo pra todo mundo.
    // O timer da SUA urna é local (cada jogador tem o próprio prazo desde a
    // recontagem do #100) — o snapshot só alimenta o overlay dos outros.
    this.urna.setPauseOverlay(snap.pause, this.urna.visible());

    // Música dinâmica: quanto maior a horda (ou boss na área), mais pesada a banda.
    // Cada quarto da partida tem seu tema; boss na arena toca a trilha do escândalo.
    const level = snap.boss || snap.enemies.length > 110 ? 3 : snap.enemies.length > 45 ? 2 : 1;
    const stage = Math.min(3, Math.floor((snap.t / this.duration) * 4));
    audio.setMood({ level, boss: !!snap.boss, bossType: snap.boss?.type, stage });

    const me = snap.players.find((p) => p.id === this.youId);
    if (me) {
      // Tomou dano? Vinheta vermelha + som de "levou paulada na CPMI"
      if (this.lastHp !== null && me.hp < this.lastHp) {
        this.hud.hurtFlash();
        audio.sfx.hurt();
      }
      this.lastHp = me.hp;
      // Embolsou um maço de Caixa 2 (#94)? Blip de transferência
      if (this.lastC2 !== null && me.c2 > this.lastC2) audio.sfx.pix();
      this.lastC2 = me.c2;
    }
    // Coletou voto (a chapa toda coleta junto)? Blip de urna
    if (this.lastXp !== null && this.lastLvl !== null
        && (snap.lvl > this.lastLvl || snap.xp > this.lastXp)) audio.sfx.gem();
    this.lastXp = snap.xp;
    this.lastLvl = snap.lvl;
  }

  // FPS sempre visível no canto superior direito (verde>50, amarelo>30, vermelho)
  private updatePerfHud(): void {
    if (!this.renderer) return;
    const fps = this.renderer.fps;
    const el = $('perf-fps');
    el.textContent = `FPS ${fps}`;
    el.style.color = fps >= 50 ? '#2ecc71' : fps >= 30 ? '#f7d117' : '#d42a2a';
  }

  private offerUrna(choices: UrnaChoiceDisplay[], time: number | undefined, charges: UrnaCharges | null): void {
    // Veio DOURADA (issue #93)? A Emenda de Relator tem fanfarra própria
    if (choices.some((c) => c.golden)) audio.sfx.urnaDourada();
    else audio.sfx.urnaKey();
    this.urna.setTimer(time ?? URNA_TIME);
    this.urna.show(choices, charges ?? { reroll: 0, banish: 0, skip: 0 }, {
      onPick: (idx) => {
        audio.sfx.urnaConfirma(); // o glorioso "FIM"
        this.net.pickUpgrade(idx);
      },
      onAction: (action, idx) => {
        audio.sfx.urnaKey();
        this.net.urnaAction(action, idx);
      },
    });
  }

  private handleEvent(ev: GameEvent): void {
    this.renderer?.onEvent(ev); // fx e bordões no mundo

    switch (ev.type) {
      case EV.URNA_OFFER: this.offerUrna(ev.choices, ev.time, ev.charges); break;
      case EV.URNA_RESULT: this.urna.hide(); break; // timer estourou? servidor votou por você
      case EV.URNA_SKIP:
        // Urna fechou sem voto: cassação esvaziou as opções (empty) OU
        // abstenção aceita (issue #100) — compensada com cura (vivo) ou
        // pontos de ranking (caído)
        this.urna.hide();
        audio.sfx.revive();
        this.hud.feed(ev.empty
          ? 'A urna ficou sem opções — sessão encerrada sem voto.'
          : ev.heal > 0
            ? `Abstenção registrada: voto em branco curou ${ev.heal} de HP.`
            : `Abstenção registrada: voto em branco rendeu ${ev.pontos} pontos.`);
        break;
      case EV.LEVEL_UP:
        audio.sfx.levelUp();
        this.hud.feed(`📈 A chapa toda subiu pro nível ${ev.level}!`);
        break;
      case EV.CASINO:
        audio.sfx.casinoOpen();
        this.casino.show(ev.prize, ev.name, ev.evoInto ?? null, {
          onTick: () => audio.sfx.casinoTick(),
          onLand: (prize) => {
            if (prize.id === 'golpe') {
              // GOLPE DE MESTRE (#98): jingle próprio + anúncio da arma suprema
              audio.sfx.casinoEvolve();
              const de = WEAPON_TABLE[ev.evoFrom ?? '']?.nome ?? 'a arma';
              const para = WEAPON_TABLE[ev.evoInto ?? '']?.nome ?? 'ARMA SUPREMA';
              // Quem evolui é o dono da receita, não necessariamente quem abriu
              this.hud.feed(`${ev.evoName ?? ev.name} deu um GOLPE DE MESTRE: ${de} evoluiu para ${para}!`, true);
            } else {
              (prize.good ? audio.sfx.casinoWin : audio.sfx.casinoLose)();
              this.hud.feed(`${ev.name} tirou ${prize.nome} no Cassino da Maleta!`);
            }
          },
        });
        break;
      case EV.BOSS_SPAWN: {
        const b = BOSS_TABLE[ev.boss];
        audio.sfx.bossAlarm();
        if (b) this.hud.setBossName(`${b.emoji} ${b.nome}`);
        this.hud.feed(ev.intro, true);
        break;
      }
      case EV.BOSS_DEATH: {
        const b = BOSS_TABLE[ev.boss];
        audio.sfx.victory();
        this.hud.feed(`💀 ${b?.nome ?? ev.boss} foi ARQUIVADO${ev.by ? ` por ${ev.by}` : ''}!`, true);
        break;
      }
      case EV.PLAYER_DOWNED:
        audio.sfx.downed();
        this.hud.feed(`☠️ ${ev.name} caiu! Chega perto pra reviver (antes que se radicalize).`);
        break;
      case EV.PLAYER_REVIVED:
        audio.sfx.revive();
        this.hud.feed(`❤️ Aliado reanimado${ev.by ? ` por ${ev.by}` : ''}! A coligação vive.`);
        break;
      case EV.PLAYER_ZOMBIFIED:
        this.hud.feed(`🧟 ${ev.name} demorou demais no chão e virou URNA POSSUÍDA!`, true);
        break;
      case EV.WAVE_CLEAR:
        audio.sfx.nova();
        this.hud.feed(`🗳️ ${ev.by} aprovou o IMPEACHMENT EXPRESS: ${ev.wiped} inimigos cassados! (o caos dobrou o spawn)`, true);
        break;
      case EV.FREEZE_SELF:
        if (ev.playerId === this.youId) {
          audio.sfx.freeze();
          this.hud.feed(`🧊 ${ev.motivo}`);
        }
        break;
      case EV.QUOTE:
        if (ev.boss) this.hud.feed(`🗣️ ${ev.text}`, true);
        break;
      case EV.FORO:
        if (ev.playerId === this.youId) this.hud.feed('🛡️ Foro Privilegiado ativado: você está IMUNE (a lei é igual pra todos*)');
        break;
      case EV.FX:
        if (ev.fx === 'nova') audio.sfx.nova();
        if (ev.fx === 'freeze') audio.sfx.freeze();
        if (ev.fx === 'pix') audio.sfx.pix();
        break;
      case EV.PLAYER_LEFT: this.hud.feed(`📴 ${ev.name} caiu da chamada (volta em até 2min)`); break;
      case EV.PLAYER_BACK: this.hud.feed(`📶 ${ev.name} voltou! A base está reconstituída.`); break;
      case EV.INFINITE_ON:
        // Alguém aceitou o MODO INFINITO (#85): fecha o card em todo mundo
        this.cleared.hide();
        audio.sfx.bossAlarm();
        this.hud.feed(`${ev.by} decretou o MODO INFINITO: o mandato agora é vitalício!`, true);
        break;
      case EV.CONQUISTA:
        // Promessa de Campanha cumprida (#99): toast pixel art discreto
        audio.sfx.levelUp();
        this.hud.conquistaToast(ev.nome, ev.votos);
        break;
    }
  }

  // ---------------------------------------------------------------------------
  // Reconexão automática
  // ---------------------------------------------------------------------------
  private async tryResume(): Promise<void> {
    const session = this.net.loadSession();
    if (!session?.code || !session?.token) return;
    const res = await this.net.reconnect(session.code, session.token);
    if (!res.ok) { this.net.clearSession(); return; }
    this.youId = res.playerId;
    if (res.resume) {
      this.startGame(res.resume.start, res.resume.snapshot,
        { choices: res.resume.pendingOffer, time: res.resume.offerTime, charges: res.resume.offerCharges },
        res.resume.cleared);
    } else if (res.state === 'ended') {
      // F5 na tela de game over (#91): reconstrói a tela com o payload que a
      // sala guardou — sessão e botão REELEIÇÃO continuam valendo. Sem payload
      // (fim silencioso), fica na abertura; o game:rematch de um colega ainda
      // alcança este cliente, que já voltou pro canal da sala.
      if (res.gameOver) {
        showScreen('game');
        this.showGameOver(res.gameOver);
      }
    } else {
      this.inLobby = true;
      showScreen('lobby');
      if (this.lastLobby) this.lobby.render(this.lastLobby, this.youId);
    }
  }

  // ---------------------------------------------------------------------------
  // Som
  // ---------------------------------------------------------------------------
  private wireAudioControls(): void {
    const ctrl = $('audio-ctrl');
    const btn = $('btn-mute');
    const slider = $input('slider-volume');

    // Ícone pixel art reflete o estado: mudo (ou volume zerado) troca as
    // ondas sonoras pelo X vermelho de mandato cassado
    const syncIcon = (): void => {
      btn.classList.toggle('muted', audio.isMuted() || audio.getVolume() === 0);
    };

    // Volume da sessão passada (bs_volume) já veio restaurado no engine
    slider.value = String(Math.round(audio.getVolume() * 100));
    syncIcon();

    // O alto-falante fica sozinho no canto: clicar abre (ou fecha) a régua
    // de volume. O controle de fato é o slider — zerar equivale a silenciar.
    const setOpen = (open: boolean): void => {
      ctrl.classList.toggle('open', open);
      btn.setAttribute('aria-expanded', String(open));
    };
    btn.onclick = (e) => {
      e.stopPropagation();
      setOpen(!ctrl.classList.contains('open'));
    };
    // Clicar fora recolhe a régua de novo
    document.addEventListener('pointerdown', (e) => {
      if (!ctrl.contains(e.target as Node)) setOpen(false);
    });

    // Arrastar o slider acima de zero desmuta na hora (a militância volta ao ar)
    slider.oninput = () => {
      const v = Number(slider.value) / 100;
      audio.setVolume(v);
      if (v > 0 && audio.isMuted()) audio.setMuted(false);
      syncIcon();
    };
    // Tenta tocar já na abertura, na cara dura: navegador com engajamento
    // prévio no site (Chrome MEI) deixa; os demais barram e caem no plano B
    audio.startMenuMusic();

    // Plano B: o 1º gesto (clique ou tecla, a abertura navega por setas)
    // destrava o áudio e liga a trilha da cena atual
    addEventListener('pointerdown', () => audio.unlock(), { once: true });
    addEventListener('keydown', () => audio.unlock(), { once: true });
  }
}
